import type { Project } from '@servicetim/shared';
import { useSetProjectArchived } from '@servicetim/ui/api';
import { Badge, Button, toast, useConfirm } from '@servicetim/ui/components';
import { Archive, ArchiveRestore, FileOutput, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { Section } from '../ui/section';
import { ExportTemplateDialog } from './export-template-dialog';
import { RenameKeyDialog } from './rename-key-dialog';

/** Key rename, template export and archive / unarchive for one project. */
export function ProjectDangerZone({ project }: { project: Project }) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const setArchived = useSetProjectArchived();
  const confirm = useConfirm();
  const archived = project.archivedAt !== null;

  const toggleArchived = async () => {
    const ok = await confirm({
      title: archived ? `Unarchive ${project.key}?` : `Archive ${project.key}?`,
      description: archived
        ? 'The project becomes visible in lists and portals again.'
        : 'Archived projects are hidden from lists and the portal; tickets and configuration are kept and the project can be unarchived later.',
      confirmLabel: archived ? 'Unarchive' : 'Archive',
      destructive: !archived,
      onConfirm: () => setArchived.mutateAsync({ id: project.id, archived: !archived }),
    });
    if (ok) toast.success(archived ? 'Project unarchived' : 'Project archived');
  };

  return (
    <Section title="Key, template and lifecycle">
      <dl className="divide-y divide-border">
        <Row
          title="Project key"
          description={
            <>
              Current key{' '}
              <Badge variant="outline" className="font-mono">
                {project.key}
              </Badge>
              . Former keys keep resolving as aliases.
            </>
          }
        >
          <Button variant="outline" onClick={() => setRenameOpen(true)}>
            <KeyRound />
            Rename key
          </Button>
        </Row>
        <Row
          title="Export as template"
          description="Save this project's configuration as a reusable template and download the JSON."
        >
          <Button variant="outline" onClick={() => setExportOpen(true)}>
            <FileOutput />
            Export template
          </Button>
        </Row>
        <Row
          title={archived ? 'Archived project' : 'Archive project'}
          description={
            archived
              ? `Archived ${new Date(project.archivedAt ?? '').toLocaleString()}.`
              : 'Hide the project from lists and the portal without deleting anything.'
          }
        >
          <Button
            variant={archived ? 'outline' : 'destructive'}
            onClick={() => void toggleArchived()}
            disabled={setArchived.isPending}
          >
            {archived ? <ArchiveRestore /> : <Archive />}
            {archived ? 'Unarchive' : 'Archive'}
          </Button>
        </Row>
      </dl>
      <RenameKeyDialog project={project} open={renameOpen} onOpenChange={setRenameOpen} />
      <ExportTemplateDialog project={project} open={exportOpen} onOpenChange={setExportOpen} />
    </Section>
  );
}

function Row({
  title,
  description,
  children,
}: {
  title: string;
  description: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0 space-y-0.5">
        <dt className="font-medium text-fg text-sm">{title}</dt>
        <dd className="text-fg-muted text-sm">{description}</dd>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
